import { XClose } from "@untitledui/icons";
import { useState } from "react";
import { toast } from "sonner";
import { useAddBrandCreators } from "@/api/admin/add-brand-creators";
import { useBrandCreators } from "@/api/admin/brand-creators";
import { useCreateLarkWebhookClient } from "@/api/admin/create-lark-webhook-client";
import { useDeleteLarkWebhookClient } from "@/api/admin/delete-lark-webhook-client";
import { useLarkWebhookClients } from "@/api/admin/lark-webhook-clients";
import { useRemoveBrandCreator } from "@/api/admin/remove-brand-creator";
import { useAdminTikTokAds } from "@/api/admin/tiktok-ads";
import { Table } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { TextArea } from "@/components/base/textarea/textarea";
import { getCreatorName } from "@/utils/creator-name";
import { normalizeCreatorId } from "@/utils/formatters";
import { pluralize } from "@/utils/text";
import { TabHeaderReadOnly } from "./tab-header";
import type { MerchantWithUsers } from "./types";

type IntegrationsTabProps = {
  merchant: MerchantWithUsers;
};

const BrandCreatorsSection = ({ merchant }: IntegrationsTabProps) => {
  const { data: brandCreators, isLoading } = useBrandCreators(merchant.id);
  const { mutateAsync: addBrandCreators, isPending: isAdding } = useAddBrandCreators();
  const { mutateAsync: removeBrandCreator } = useRemoveBrandCreator();

  const [creatorIdsText, setCreatorIdsText] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);

  const handleAddCreators = async () => {
    const creatorIds = creatorIdsText
      .split(/[\n,]+/)
      .map((id) => normalizeCreatorId(id))
      .filter((id) => id.length > 4);

    if (creatorIds.length === 0) {
      toast.error("No creator IDs provided");
      return;
    }

    try {
      const { failedToInsert } = await addBrandCreators({
        merchantId: merchant.id,
        creatorIds,
      });

      if (failedToInsert.length > 0) {
        toast.error(
          `${pluralize(failedToInsert.length, "creator", "creators")} not found: ${failedToInsert.join(", ")}`,
        );
      } else {
        toast.success("Merchant creators added");
      }
    } catch {
      toast.error("Failed to add merchant creators");
      return;
    }

    setCreatorIdsText("");
    setIsFormOpen(false);
  };

  const handleRemoveCreator = async (creatorId: string) => {
    try {
      await removeBrandCreator({ merchantId: merchant.id, creatorId });
      toast.success("Merchant creator removed");
    } catch {
      toast.error("Failed to remove merchant creator");
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-tertiary">Loading merchant creators...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row gap-8">
        <div className="w-80 flex flex-col gap-1">
          <p className="text-sm font-medium text-primary">Marketing Accounts</p>
          <p className="text-sm text-tertiary">
            TikTok accounts owned by the merchant. Adding these allows us to exclude them from reports and enable
            filtering.
          </p>
        </div>
        <div className="flex-1">
          {brandCreators && brandCreators.length > 0 ? (
            <Table aria-label="Merchant Creators">
              <Table.Header className="rounded-lg">
                <Table.Head id="creator" label="Creator" isRowHeader className="w-4/5" />
                <Table.Head id="actions" label="" className="w-1/5" />
              </Table.Header>

              <Table.Body items={brandCreators}>
                {(item) => {
                  const { nickname, handle } = getCreatorName(item.creator.nickname, item.creator.creatorName);
                  return (
                    <Table.Row id={item.creatorId}>
                      <Table.Cell className="whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {item.creator.thumbnail && (
                            <img
                              src={item.creator.thumbnail}
                              alt={item.creator.creatorName}
                              className="h-8 w-8 rounded-full object-cover"
                            />
                          )}
                          <div>
                            <p className="text-sm font-medium text-primary">{nickname ?? handle}</p>
                            {nickname && <p className="text-sm text-tertiary">{handle}</p>}
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap text-right">
                        <ButtonUtility
                          size="xs"
                          color="secondary"
                          tooltip="Remove"
                          icon={XClose}
                          onClick={() => handleRemoveCreator(item.creatorId)}
                        />
                      </Table.Cell>
                    </Table.Row>
                  );
                }}
              </Table.Body>
            </Table>
          ) : (
            <div className="flex items-center justify-center py-6 border border-dashed border-secondary rounded-lg">
              <p className="text-sm text-tertiary">No merchant creators configured</p>
            </div>
          )}

          {isFormOpen ? (
            <div className="mt-4 p-4 border border-secondary rounded-lg">
              <TextArea
                label="Creator IDs"
                placeholder="Enter creator IDs, separated by newlines or commas"
                value={creatorIdsText}
                onChange={setCreatorIdsText}
                rows={4}
              />
              <div className="flex gap-2 mt-4 justify-end">
                <Button color="secondary" size="sm" onClick={() => setIsFormOpen(false)}>
                  Cancel
                </Button>
                <Button color="primary" size="sm" onClick={handleAddCreators} isDisabled={isAdding}>
                  {isAdding ? "Adding..." : "Add Creators"}
                </Button>
              </div>
            </div>
          ) : (
            <Button color="secondary" size="sm" className="mt-4" onClick={() => setIsFormOpen(true)}>
              Add Merchant Creators
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const LarkWebhooksSection = ({ merchant }: IntegrationsTabProps) => {
  const { data: larkClients, isLoading } = useLarkWebhookClients(merchant.id);
  const { mutateAsync: createLarkClient, isPending: isCreating } = useCreateLarkWebhookClient();
  const { mutateAsync: deleteLarkClient } = useDeleteLarkWebhookClient();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [signatureKey, setSignatureKey] = useState("");

  const handleAddWebhook = async () => {
    if (!webhookUrl.trim()) {
      toast.error("Webhook URL is required");
      return;
    }
    if (!signatureKey.trim()) {
      toast.error("Signature Key is required");
      return;
    }

    try {
      await createLarkClient({
        merchantId: merchant.id,
        webhookUrl: webhookUrl.trim(),
        signatureKey: signatureKey.trim(),
      });
      toast.success("Webhook added successfully");
      setWebhookUrl("");
      setSignatureKey("");
      setIsFormOpen(false);
    } catch {
      toast.error("Failed to add webhook");
    }
  };

  const handleRemoveWebhook = async (id: string) => {
    try {
      await deleteLarkClient({ id, merchantId: merchant.id });
      toast.success("Webhook removed");
    } catch {
      toast.error("Failed to remove webhook");
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-tertiary">Loading webhooks...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row gap-8">
        <div className="w-80 flex flex-col gap-1">
          <p className="text-sm font-medium text-primary">Lark Webhooks</p>
          <p className="text-sm text-tertiary">Webhook URLs for Lark notifications when safety issues are detected.</p>
        </div>
        <div className="flex-1">
          {larkClients && larkClients.length > 0 ? (
            <Table aria-label="Lark Webhooks">
              <Table.Header className="rounded-lg">
                <Table.Head id="url" label="Webhook URL" isRowHeader className="w-4/5" />
                <Table.Head id="actions" label="" className="w-1/5" />
              </Table.Header>

              <Table.Body items={larkClients}>
                {(item) => (
                  <Table.Row id={item.id}>
                    <Table.Cell className="whitespace-nowrap">
                      <p className="text-sm font-medium text-primary truncate max-w-md" title={item.webhookUrl}>
                        {item.webhookUrl}
                      </p>
                      <p className="text-xs text-tertiary">Added {new Date(item.createdAt).toLocaleDateString()}</p>
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap text-right">
                      <ButtonUtility
                        size="xs"
                        color="secondary"
                        tooltip="Remove"
                        icon={XClose}
                        onClick={() => handleRemoveWebhook(item.id)}
                      />
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          ) : (
            <div className="flex items-center justify-center py-6 border border-dashed border-secondary rounded-lg">
              <p className="text-sm text-tertiary">No webhooks configured</p>
            </div>
          )}

          {isFormOpen ? (
            <div className="mt-4 p-4 border border-secondary rounded-lg flex flex-col gap-4">
              <div>
                <label htmlFor="webhook-url" className="block text-sm font-medium text-primary mb-1">
                  Webhook URL
                </label>
                <input
                  id="webhook-url"
                  type="text"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://open.larksuite.com/open-apis/bot/v2/hook/..."
                  className="w-full px-3 py-2 text-sm text-primary bg-primary rounded-lg ring-1 ring-primary outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="signature-key" className="block text-sm font-medium text-primary mb-1">
                  Signature Key
                </label>
                <input
                  id="signature-key"
                  type="text"
                  value={signatureKey}
                  onChange={(e) => setSignatureKey(e.target.value)}
                  placeholder="Enter signature key"
                  className="w-full px-3 py-2 text-sm text-primary bg-primary rounded-lg ring-1 ring-primary outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button color="secondary" size="sm" onClick={() => setIsFormOpen(false)}>
                  Cancel
                </Button>
                <Button color="primary" size="sm" onClick={handleAddWebhook} isDisabled={isCreating}>
                  {isCreating ? "Adding..." : "Add Webhook"}
                </Button>
              </div>
            </div>
          ) : (
            <Button color="secondary" size="sm" className="mt-4" onClick={() => setIsFormOpen(true)}>
              Add Lark Webhook
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const TikTokAdsSection = ({ merchant }: IntegrationsTabProps) => {
  const { data, isLoading, error } = useAdminTikTokAds(merchant.id);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row gap-8">
        <div className="w-80 flex flex-col gap-1">
          <p className="text-sm font-medium text-primary">TikTok Ads</p>
          <p className="text-sm text-tertiary">
            Connection status, advertisers, and granted permissions for this merchant's TikTok Ads account.
          </p>
        </div>
        <div className="flex-1 flex flex-col gap-4">
          {isLoading ? (
            <p className="text-sm text-tertiary">Loading TikTok Ads details...</p>
          ) : error ? (
            <p className="text-sm text-error-primary">Failed to load TikTok Ads details</p>
          ) : !data ? (
            <p className="text-sm text-tertiary">No data</p>
          ) : (
            <>
              <div className="flex items-center gap-2">
                {data.connected ? <Badge color="success">Connected</Badge> : <Badge color="gray">Not connected</Badge>}
                {data.needsReauth && <Badge color="warning">Needs re-auth</Badge>}
              </div>

              {data.error && <p className="text-sm text-error-primary">{data.error}</p>}

              {data.scopes.length > 0 && (
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium text-primary">Granted scopes</p>
                  <div className="flex flex-row flex-wrap gap-1">
                    {data.scopes.map((scope) => (
                      <Badge color="gray-blue" key={scope}>
                        {scope}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {data.advertisers.length > 0 ? (
                <Table aria-label="Advertisers">
                  <Table.Header className="rounded-lg">
                    <Table.Head id="advertiser_id" label="Advertiser ID" isRowHeader className="w-1/3" />
                    <Table.Head id="name" label="Name" className="w-1/3" />
                    <Table.Head id="status" label="Status" className="w-1/3" />
                  </Table.Header>

                  <Table.Body items={data.advertisers.map((a) => ({ ...a, id: a.advertiser_id }))}>
                    {(item) => (
                      <Table.Row id={item.advertiser_id}>
                        <Table.Cell className="whitespace-nowrap">
                          <p className="text-sm font-medium text-primary">{item.advertiser_id}</p>
                          {item.country && <p className="text-xs text-tertiary">{item.country}</p>}
                        </Table.Cell>
                        <Table.Cell className="whitespace-nowrap">
                          <p className="text-sm text-primary">{item.name ?? "—"}</p>
                          {item.company && <p className="text-xs text-tertiary">{item.company}</p>}
                        </Table.Cell>
                        <Table.Cell className="whitespace-nowrap">
                          <p className="text-sm text-primary">{item.status ?? "—"}</p>
                          {item.currency && <p className="text-xs text-tertiary">{item.currency}</p>}
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table>
              ) : data.advertiserIds.length > 0 ? (
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium text-primary">Advertiser IDs</p>
                  <div className="flex flex-row flex-wrap gap-1">
                    {data.advertiserIds.map((id) => (
                      <Badge color="gray" key={id}>
                        {id}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const IntegrationsContent = ({ merchant }: IntegrationsTabProps) => {
  return (
    <div className="flex flex-col gap-6">
      <TabHeaderReadOnly
        title="Integrations"
        subtitle="Manage external integrations and marketing accounts for this merchant"
      />

      <div className="border-t border-primary" />

      <TikTokAdsSection merchant={merchant} />

      <div className="border-t border-primary" />

      <BrandCreatorsSection merchant={merchant} />

      <div className="border-t border-primary" />

      <LarkWebhooksSection merchant={merchant} />
    </div>
  );
};

export const IntegrationsTab = ({ merchant }: IntegrationsTabProps) => (
  <IntegrationsContent key={merchant.id} merchant={merchant} />
);
